/**
 * Relationship content type for friend/block functionality
 */
import type { Identity } from '../identity/identity.js';
import type { DAGObject, RelationshipContent } from '../dag/types.js';

export interface CreateRelationshipOptions {
  target: string;
  type: 'friend' | 'block';
  action: 'add' | 'remove';
}

/**
 * Create a relationship object
 */
export function createRelationship(
  identity: Identity,
  options: CreateRelationshipOptions
): DAGObject<RelationshipContent> {
  const content: RelationshipContent = {
    target: options.target,
    type: options.type,
    action: options.action,
  };
  return identity.createObject('relationship', content);
}

/**
 * Type guard for relationship objects
 */
export function isRelationship(obj: DAGObject): obj is DAGObject<RelationshipContent> {
  return obj.type === 'relationship';
}

/**
 * Add a friend
 */
export function addFriend(identity: Identity, targetPubkey: string): DAGObject<RelationshipContent> {
  return createRelationship(identity, {
    target: targetPubkey,
    type: 'friend',
    action: 'add',
  });
}

/**
 * Remove a friend
 */
export function removeFriend(identity: Identity, targetPubkey: string): DAGObject<RelationshipContent> {
  return createRelationship(identity, {
    target: targetPubkey,
    type: 'friend',
    action: 'remove',
  });
}

/**
 * Block a user
 */
export function blockUser(identity: Identity, targetPubkey: string): DAGObject<RelationshipContent> {
  return createRelationship(identity, {
    target: targetPubkey,
    type: 'block',
    action: 'add',
  });
}

/**
 * Unblock a user
 */
export function unblockUser(identity: Identity, targetPubkey: string): DAGObject<RelationshipContent> {
  return createRelationship(identity, {
    target: targetPubkey,
    type: 'block',
    action: 'remove',
  });
}

/**
 * Process relationship objects to get current state
 * Returns the current friends and blocked sets based on most recent actions
 */
export function processRelationships(
  relationships: DAGObject<RelationshipContent>[]
): { friends: Set<string>; blocked: Set<string> } {
  // Sort by timestamp, newest first
  const sorted = [...relationships].sort((a, b) => b.timestamp - a.timestamp);

  const friends = new Set<string>();
  const blocked = new Set<string>();
  const processedFriends = new Set<string>();
  const processedBlocks = new Set<string>();

  for (const rel of sorted) {
    const { target, type, action } = rel.content;

    if (type === 'friend' && !processedFriends.has(target)) {
      processedFriends.add(target);
      if (action === 'add') {
        friends.add(target);
      }
    } else if (type === 'block' && !processedBlocks.has(target)) {
      processedBlocks.add(target);
      if (action === 'add') {
        blocked.add(target);
      }
    }
  }

  return { friends, blocked };
}
